/**
 * FilterPane: the Power BI style pane beside a table that lists every filter
 * currently narrowing it, one chip per value, each removable, with a Clear
 * all. Collapses to a narrow rail carrying the funnel and the active count.
 *
 * Presentational and reusable. `groups` is [{ key, label, items: [{ value,
 * label }] }]; `onRemove(key, value)` drops one value, `onClearAll()` drops
 * everything. `children` renders extra controls in the pane (a date range,
 * say) under the chips.
 */
import React from "react";
import "./table.css";

const FilterPane = ({ open, onToggle, groups = [], onRemove, onClearAll, children, title = "Filters" }) => {
  const active = groups.reduce((n, g) => n + g.items.length, 0);

  if (!open) {
    return (
      <button
        type="button"
        className={`lk-filterpane-rail${active ? " is-active" : ""}`}
        onClick={onToggle}
        aria-label={`Show filters${active ? `, ${active} active` : ""}`}
        title="Show filters"
      >
        <i className="fas fa-filter" aria-hidden="true" />
        {active > 0 && <span className="lk-filterpane-count">{active}</span>}
        <span className="lk-filterpane-rail-label">{title}</span>
      </button>
    );
  }

  return (
    <aside className="lk-filterpane" aria-label={title}>
      <div className="lk-filterpane-head">
        <span>
          <i className="fas fa-filter" aria-hidden="true" /> {title}
          {active > 0 && <span className="lk-filterpane-count">{active}</span>}
        </span>
        <button type="button" className="lk-filterpane-collapse" onClick={onToggle} aria-label="Hide filters" title="Hide filters">
          <i className="fas fa-angle-double-right" aria-hidden="true" />
        </button>
      </div>
      {active === 0 && !children && <div className="lk-filterpane-empty">No filters on. Use the funnel on a column header.</div>}
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <div key={g.key} className="lk-filterpane-group">
            <div className="lk-filterpane-label">{g.label}</div>
            <div className="lk-filterpane-chips">
              {g.items.map((it) => (
                <span key={it.value} className="lk-filterpane-chip">
                  {it.label}
                  <button
                    type="button"
                    onClick={() => onRemove(g.key, it.value)}
                    aria-label={`Remove ${g.label} ${it.label}`}
                    title="Remove"
                  >
                    <i className="fas fa-times" aria-hidden="true" />
                  </button>
                </span>
              ))}
            </div>
          </div>
        ))}
      {children}
      {active > 0 && (
        <button type="button" className="lk-filterpane-clear" onClick={onClearAll}>
          <i className="fas fa-times" aria-hidden="true" /> Clear all filters
        </button>
      )}
    </aside>
  );
};

export default FilterPane;
